import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListAdminBlogPosts,
  useCreateBlogPost,
  useUpdateBlogPost,
  getListAdminBlogPostsQueryKey,
  getListBlogPostsQueryKey,
  getListBlogRevisionsQueryKey,
  type BlogPost,
} from '@workspace/api-client-react';
import { useAuth } from '@workspace/replit-auth-web';
import { Plus, LogOut, RotateCcw, AlertTriangle } from 'lucide-react';
import { BlogEntry, formatLogDate } from '@/components/blog/BlogEntry';
import { BlogRecovery } from '@/components/blog/BlogRecovery';
import { BlogReleases } from '@/components/blog/BlogReleases';
import { BlogMeshes } from '@/pages/Blog';
import { usePageMeta } from '@/hooks/use-page-meta';

type Status = 'draft' | 'published';
interface Draft { title: string; logDate: string; location: string; body: string; publishAt: string }

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const emptyDraft = (): Draft => ({ title: '', logDate: today(), location: '', body: '', publishAt: '' });
const fromPost = (p: BlogPost): Draft => ({ title: p.title ?? '', logDate: p.logDate, location: p.location, body: p.body, publishAt: p.publishAt ?? '' });
const sameDraft = (a: Draft, b: Draft) =>
  a.title === b.title && a.logDate === b.logDate && a.location === b.location && a.body === b.body && a.publishAt === b.publishAt;

function errStatus(e: unknown): number | undefined {
  return typeof e === 'object' && e && 'status' in e ? Number((e as { status: unknown }).status) : undefined;
}
function errData(e: unknown): { error?: string; current?: BlogPost } | null {
  return typeof e === 'object' && e && 'data' in e ? ((e as { data: unknown }).data as { error?: string; current?: BlogPost } | null) : null;
}

const label = 'block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2';
const field = 'w-full bg-white/80 border border-border px-3 py-2.5 text-[15px] text-foreground focus:outline-none focus:border-foreground transition-colors';
const btn = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const btnSolid = `${btn} bg-foreground text-background hover:bg-primary hover:text-primary-foreground`;
const btnLine = `${btn} border border-foreground/80 text-foreground hover:bg-foreground hover:text-background`;
const btnQuiet = `${btn} text-muted-foreground hover:text-foreground`;

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative overflow-hidden min-h-[100dvh] pt-32 md:pt-40 pb-24">
      <BlogMeshes />
      <div className="relative z-10 container mx-auto px-6 md:px-12">{children}</div>
    </main>
  );
}

function Notice({ title, children, testId }: { title: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="max-w-md" data-testid={testId}>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-foreground">Blog editor</p>
      <div className="mt-4 h-px w-10 bg-primary" />
      <h1 className="mt-10 text-2xl font-medium tracking-tight">{title}</h1>
      <div className="mt-4 text-muted-foreground leading-relaxed space-y-6">{children}</div>
    </div>
  );
}

export default function AdminBlog() {
  usePageMeta('Blog Editor | Plasmics', 'Private editor for the Plasmics founder log.', true);
  const auth = useAuth();

  if (auth.isLoading) {
    return (
      <Shell>
        <div className="space-y-4 max-w-md" aria-busy="true" aria-label="Checking sign-in">
          <div className="h-3 w-32 bg-muted animate-pulse" />
          <div className="h-7 w-64 bg-muted animate-pulse" />
          <div className="h-3 w-full bg-muted animate-pulse" />
        </div>
      </Shell>
    );
  }

  if (auth.error && !auth.isAuthenticated) {
    return (
      <Shell>
        <Notice title="Sign-in status unavailable" testId="status-auth-error">
          <p>We could not confirm whether you are signed in.</p>
          <button type="button" className={btnLine} onClick={() => auth.refresh()} data-testid="button-auth-retry">
            <RotateCcw size={14} /> Try again
          </button>
        </Notice>
      </Shell>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Shell>
        <Notice title="Sign in to edit the log" testId="status-signed-out">
          <p>Drafts and editing are limited to approved editors.</p>
          <button type="button" className={btnSolid} onClick={() => auth.login()} data-testid="button-login">
            Log in
          </button>
        </Notice>
      </Shell>
    );
  }

  if (!auth.isEditor) {
    return (
      <Shell>
        <Notice title="No editor access" testId="status-not-editor">
          <p>
            You are signed in{auth.user?.name ? ` as ${auth.user.name}` : ''}, but this account is not on the editor list.
            Ask the site owner to add your account identifier to the editor configuration.
          </p>
          {auth.user?.id && (
            <p className="border border-border bg-white/80 px-4 py-3">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em]">Account identifier</span>
              <code className="mt-1 block font-mono text-sm text-foreground select-all break-all" data-testid="text-user-id">{auth.user.id}</code>
            </p>
          )}
          <button type="button" className={btnLine} onClick={() => auth.logout()} data-testid="button-logout">
            <LogOut size={14} /> Log out
          </button>
          {auth.error && <p role="alert" className="text-destructive">{auth.error}</p>}
        </Notice>
      </Shell>
    );
  }

  return <Editor csrfToken={auth.csrfToken ?? ''} userName={auth.user?.name ?? null} authError={auth.error} onLogout={() => auth.logout()} onLogin={() => auth.login()} />;
}

function Editor({ csrfToken, userName, authError, onLogout, onLogin }: { csrfToken: string; userName: string | null; authError: string | null; onLogout: () => void; onLogin: () => void }) {
  const queryClient = useQueryClient();
  const list = useListAdminBlogPosts();
  const request = { headers: { 'X-CSRF-Token': csrfToken } };
  const create = useCreateBlogPost({ request });
  const update = useUpdateBlogPost({ request });

  const [selectedId, setSelectedId] = useState<string | null>(null); // null = new entry
  const [baseRevision, setBaseRevision] = useState<number | null>(null);
  const [baseline, setBaseline] = useState<Draft>(emptyDraft);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const [conflict, setConflict] = useState<BlogPost | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [restoring, setRestoring] = useState(false);

  const posts = list.data?.posts ?? [];
  const selected = useMemo(() => posts.find(p => p.id === selectedId) ?? null, [posts, selectedId]);
  const dirty = !sameDraft(draft, baseline);
  const saving = create.isPending || update.isPending || restoring;
  const listStatus = errStatus(list.error);

  useEffect(() => {
    if (!dirty && !saving) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  const confirmDiscard = () => !dirty || window.confirm('Discard unsaved changes to this entry?');

  const open = (post: BlogPost | null) => {
    if (saving || !confirmDiscard()) return;
    const d = post ? fromPost(post) : emptyDraft();
    setSelectedId(post?.id ?? null);
    setBaseRevision(post?.revision ?? null);
    setBaseline(d);
    setDraft(d);
    setConflict(null);
    setMessage(null);
    setFieldErrors({});
    setMode('write');
  };

  const loadServerVersion = () => {
    if (!conflict) return;
    if (!window.confirm('Replace your unsaved text with the newer saved version?')) return;
    const d = fromPost(conflict);
    setBaseRevision(conflict.revision);
    setBaseline(d);
    setDraft(d);
    setConflict(null);
    setMessage({ kind: 'ok', text: `Loaded revision ${conflict.revision}.` });
  };

  const keepMine = () => {
    if (!conflict) return;
    setBaseRevision(conflict.revision);
    setBaseline(fromPost(conflict));
    setConflict(null);
    setMessage({ kind: 'ok', text: 'Your text is kept. Saving now will replace the newer version.' });
  };

  const validate = () => {
    const e: Partial<Record<keyof Draft, string>> = {};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.logDate) || Number.isNaN(Date.parse(draft.logDate))) e.logDate = 'Use a real date.';
    if (!draft.location.trim()) e.location = 'Location is required.';
    if (!draft.body.trim()) e.body = 'Write something first.';
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = (status: Status) => {
    if (conflict || !validate()) return;
    setMessage(null);
    const payload = {
      title: draft.title.trim() ? draft.title.trim() : null,
      logDate: draft.logDate,
      location: draft.location.trim(),
      body: draft.body,
      status,
      publishAt: draft.publishAt || null,
    };
    const done = (post: BlogPost) => {
      queryClient.invalidateQueries({ queryKey: getListBlogRevisionsQueryKey(post.id) });
      queryClient.invalidateQueries({ queryKey: getListAdminBlogPostsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListBlogPostsQueryKey() });
      const d = fromPost(post);
      setSelectedId(post.id);
      setBaseRevision(post.revision);
      setBaseline(d);
      setDraft(d);
      setMessage({
        kind: 'ok',
        text: post.status === 'published' ? post.publishAt && new Date(post.publishAt) > new Date() ? `Scheduled for ${post.publishAt}. Not public yet.` : 'Published. It is live on the blog.' : selected?.status === 'published' && status === 'draft' ? 'Unpublished. Saved as draft.' : 'Draft saved.',
      });
    };
    const failed = (e: unknown) => {
      const s = errStatus(e);
      const data = errData(e);
      if (s === 409 && data?.current) {
        setConflict(data.current);
        queryClient.invalidateQueries({ queryKey: getListAdminBlogPostsQueryKey() });
        return;
      }
      if (s === 401) setMessage({ kind: 'error', text: 'Your session ended. Copy your text, then log in again.' });
      else if (s === 403) setMessage({ kind: 'error', text: 'This request was refused. Reload the page to refresh your session, keeping a copy of your text.' });
      else setMessage({ kind: 'error', text: data?.error ?? 'Saving failed. Your text is still here; try again.' });
    };
    if (selectedId && baseRevision !== null) {
      update.mutate({ id: selectedId, data: { ...payload, baseRevision } }, { onSuccess: done, onError: failed });
    } else {
      create.mutate({ data: payload }, { onSuccess: done, onError: failed });
    }
  };

  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft(d => ({ ...d, [k]: e.target.value }));

  const isPublished = selected?.status === 'published';

  return (
    <Shell>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <h1 className="font-mono text-xs uppercase tracking-[0.3em] text-foreground">Blog editor</h1>
          <div className="mt-4 h-px w-10 bg-primary" />
        </div>
        <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {userName && <span data-testid="text-editor-name">{userName}</span>}
          <button type="button" className={btnQuiet} disabled={saving} onClick={() => confirmDiscard() && onLogout()} data-testid="button-logout">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>

      <BlogReleases csrfToken={csrfToken} disabled={saving || !sameDraft(draft, baseline)} onChanged={() => {
        setSelectedId(null); setBaseRevision(null); setDraft(emptyDraft()); setBaseline(emptyDraft());
      }} />
      <div className="grid gap-12 lg:grid-cols-[17rem_1fr]">
        {authError && <p role="alert" className="text-destructive lg:col-span-2">{authError}</p>}
        {/* Post list */}
        <aside aria-label="Entries">
          <button type="button" className={`${btnLine} w-full`} disabled={saving} onClick={() => open(null)} data-testid="button-new-entry">
            <Plus size={14} /> New entry
          </button>
          <div className="mt-8">
            {list.isLoading && (
              <div className="space-y-5" aria-busy="true">
                {[0, 1, 2].map(i => <div key={i} className="space-y-2"><div className="h-2.5 w-24 bg-muted animate-pulse" /><div className="h-4 w-full bg-muted animate-pulse" /></div>)}
              </div>
            )}
            {list.isError && (
              <div role="alert" className="text-sm text-muted-foreground space-y-3" data-testid="status-list-error">
                <p>{listStatus === 401 ? 'Your session ended.' : listStatus === 403 ? 'This account cannot read drafts.' : 'Entries could not be loaded.'}</p>
                {listStatus === 401
                  ? <button type="button" className={btnQuiet + ' px-0'} onClick={onLogin} data-testid="button-relogin">Log in again</button>
                  : <button type="button" className={btnQuiet + ' px-0'} onClick={() => list.refetch()} data-testid="button-list-retry"><RotateCcw size={13} /> Retry</button>}
              </div>
            )}
            {!list.isLoading && !list.isError && posts.length === 0 && (
              <p className="text-sm text-muted-foreground leading-relaxed" data-testid="status-list-empty">No entries yet. Start one with New entry.</p>
            )}
            <ul className="divide-y divide-border">
              {posts.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={saving} onClick={() => p.id !== selectedId && open(p)}
                    aria-current={p.id === selectedId ? 'true' : undefined}
                    className={`w-full text-left py-4 pl-3 border-l-2 transition-colors ${p.id === selectedId ? 'border-primary' : 'border-transparent hover:border-border'}`}
                    data-testid={`button-entry-${p.id}`}
                  >
                    <span className="flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      <span>{formatLogDate(p.logDate)}</span>
                      <span className={p.status === 'published' ? 'text-secondary' : 'text-primary'} data-testid={`status-entry-${p.id}`}>{p.status === 'published' && p.publishAt && new Date(p.publishAt) > new Date() ? 'scheduled' : p.status}</span>
                    </span>
                    <span className="mt-1.5 block text-sm text-foreground truncate">{p.title || p.location}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Editor */}
        <section aria-label="Entry editor" className="min-w-0">
          <fieldset disabled={saving} className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="text-editor-state">
              {selected ? `${selected.status} · revision ${baseRevision}` : 'New entry · not saved'}
              {dirty && <span className="text-primary"> · unsaved changes</span>}
            </p>
            <div role="tablist" className="flex border border-border">
              {(['write', 'preview'] as const).map(m => (
                <button key={m} type="button" role="tab" aria-selected={mode === m} disabled={saving} onClick={() => setMode(m)}
                  className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${mode === m ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`tab-${m}`}>{m}</button>
              ))}
            </div>
          </div>

          {conflict && (
            <div role="alert" className="mb-8 border border-primary/60 bg-white/85 p-5" data-testid="status-conflict">
              <p className="flex items-center gap-2 font-medium text-foreground"><AlertTriangle size={16} className="text-primary" /> This entry changed elsewhere</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Revision {conflict.revision} was saved {new Date(conflict.updatedAt).toLocaleString()} ({conflict.status}). Nothing was overwritten, and your unsaved text is still below.
              </p>
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-widest text-muted-foreground">View the newer version</summary>
                <div className="mt-4 border-l border-border pl-4"><BlogEntry post={conflict} /></div>
              </details>
              <div className="mt-5 flex flex-wrap gap-3">
                <button type="button" className={btnLine} disabled={saving} onClick={loadServerVersion} data-testid="button-conflict-reload">Load newer version</button>
                <button type="button" className={btnQuiet} disabled={saving} onClick={keepMine} data-testid="button-conflict-keep">Keep my text</button>
              </div>
            </div>
          )}

          {mode === 'write' ? (
            <fieldset disabled={saving} aria-busy={saving} className="space-y-6 min-w-0 disabled:opacity-70">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="logDate" className={label}>Log date</label>
                  <input id="logDate" type="date" required value={draft.logDate} onChange={set('logDate')} className={field} aria-invalid={!!fieldErrors.logDate} data-testid="input-log-date" />
                  {fieldErrors.logDate && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.logDate}</p>}
                </div>
                <div>
                  <label htmlFor="location" className={label}>Location</label>
                  <input id="location" required value={draft.location} onChange={set('location')} placeholder="City or site" className={field} aria-invalid={!!fieldErrors.location} data-testid="input-location" />
                  {fieldErrors.location && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.location}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="title" className={label}>Title <span className="normal-case tracking-normal">(optional)</span></label>
                <input id="title" value={draft.title} onChange={set('title')} className={field} data-testid="input-title" />
              </div>
              <div>
                <label htmlFor="publishAt" className={label}>Publication time (optional, UTC)</label>
                <input id="publishAt" type="datetime-local" className={field} data-testid="input-publish-at"
                  value={draft.publishAt ? draft.publishAt.slice(0, 16) : ''}
                  onChange={e => setDraft(d => ({ ...d, publishAt: e.target.value ? `${e.target.value}:00.000Z` : '' }))} />
                <p className="mt-2 text-xs text-muted-foreground">Leave blank to publish immediately. A future UTC time keeps a published entry private until then. Saving as draft always keeps it private.</p>
              </div>
              <div>
                <label htmlFor="body" className={label}>Body <span className="normal-case tracking-normal">(Markdown, HTML is not rendered)</span></label>
                <textarea id="body" required value={draft.body} onChange={set('body')} rows={18}
                  className={`${field} font-mono text-[13.5px] leading-relaxed resize-y min-h-[20rem]`} aria-invalid={!!fieldErrors.body} data-testid="input-body" />
                {fieldErrors.body && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.body}</p>}
              </div>
            </fieldset>
          ) : (
            <div className="border border-border bg-white/70 px-6 py-12 md:px-12" data-testid="panel-preview">
              <p className="mb-10 font-mono text-[10px] uppercase tracking-[0.22em] text-primary">Preview · as it will appear</p>
              <div className="max-w-[38rem] mx-auto">
                {draft.body.trim() || draft.location.trim()
                  ? <BlogEntry post={{ title: draft.title.trim() || null, logDate: draft.logDate, location: draft.location.trim() || 'Location', body: draft.body }} />
                  : <p className="text-muted-foreground">Nothing to preview yet.</p>}
              </div>
            </div>
          )}

          {message && (
            <p role={message.kind === 'error' ? 'alert' : 'status'} className={`mt-6 text-sm ${message.kind === 'error' ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="status-save-message">
              {message.text}
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-3 border-t border-border pt-6">
            {isPublished ? (
              <>
                <button type="button" className={btnSolid} disabled={saving || !!conflict || !dirty} onClick={() => save('published')} data-testid="button-update-published">
                  {saving ? 'Saving' : 'Update published'}
                </button>
                <button type="button" className={btnLine} disabled={saving || !!conflict} onClick={() => save('draft')} data-testid="button-unpublish">Unpublish</button>
              </>
            ) : (
              <>
                <button type="button" className={btnLine} disabled={saving || !!conflict || (!!selected && !dirty)} onClick={() => save('draft')} data-testid="button-save-draft">
                  {saving ? 'Saving' : 'Save draft'}
                </button>
                <button type="button" className={btnSolid} disabled={saving || !!conflict} onClick={() => save('published')} data-testid="button-publish">Publish</button>
              </>
            )}
            {dirty && (
              <button type="button" className={btnQuiet} disabled={saving} onClick={() => window.confirm('Revert to the last saved text?') && setDraft(baseline)} data-testid="button-revert">Revert</button>
            )}
          </div>
          </fieldset>
          {selectedId && baseRevision !== null && <BlogRecovery
            key={selectedId} id={selectedId} baseRevision={baseRevision} csrfToken={csrfToken}
            blocked={saving || dirty || !!conflict} onBusy={setRestoring}
            onRestored={post => {
              const d = fromPost(post);
              setBaseRevision(post.revision);
              setBaseline(d); setDraft(d); setConflict(null); setFieldErrors({}); setMode('write');
              setMessage({ kind: 'ok', text: `Restored as draft revision ${post.revision}. Review and publish separately. The replaced version is in Saved versions.` });
            }}
            onError={error => {
              const data = errData(error);
              if (errStatus(error) === 409 && data?.current) {
                setConflict(data.current);
                queryClient.invalidateQueries({ queryKey: getListAdminBlogPostsQueryKey() });
              }
              setMessage({ kind: 'error', text: data?.error ?? 'Could not confirm the restore. Keep a copy of your text and reload the entry to check its saved version before retrying.' });
            }}
          />}
        </section>
      </div>
    </Shell>
  );
}
